import { useRefs } from '@/scripts/hooks';
import {
  ChatMessagePayloadType,
  ChatMessageStatus,
} from '@/contexts/chat/scripts/hooks/useChatMessages/types';
import { ChatCategory } from '@/contexts/chat/scripts/hooks/useChats/types';
import { useSessionContext } from '@/contexts/session/context';
import { GroupStatus } from '@/configs/apis/groups/interface';
import styles from './index.module.scss';
import CallIcon from './images/call.svg?react';
import VideoIcon from './images/video.svg?react';
import type { GroupJoinCallProps } from './interface';

const SHOW_COUNT = 10;

export const GroupJoinCall = ({
  chat,
  chatMediaCall,
  mediaJoin,
  chatGroup,
}: GroupJoinCallProps) => {
  const { session } = useSessionContext();
  const [loading, setLoading] = useState(false);

  const refs = useRefs({ chatMediaCall });

  const group = useMemo(
    () => chatGroup.chatGroupState.state.groupMap[chat.chat_id],
    [chatGroup.chatGroupState.state.groupMap, chat.chat_id]
  );

  const isGroup = useMemo(() => chat.category === ChatCategory.GROUP, [chat.category]);

  const normalMemberList = useMemo(
    () => chatGroup.chatGroupState.state.membersMap[chat.chat_id]?.normalMembers,
    [chatGroup.chatGroupState.state.membersMap, chat.chat_id]
  );

  const isGroupLeave = useMemo(() => {
    const inGroup = normalMemberList?.some(
      (member) => member.user_id === session?.user?.userId
    );
    return isGroup && !inGroup;
  }, [isGroup, normalMemberList, session?.user?.userId]);

  const isGroupDisband = useMemo(() => {
    return isGroup && group?.status === GroupStatus.DISBAND;
  }, [isGroup, group]);

  const onGoingChatMediaCall = useMemo(() => {
    if (isGroupLeave || isGroupDisband) {
      return null;
    }
    return chatMediaCall.chatMediaInfoMap[chat.chat_id];
  }, [chat.chat_id, chatMediaCall, isGroupLeave, isGroupDisband]);

  const showAccept = useMemo(() => {
    const inOtherCall =
      chatMediaCall.activatedId &&
      chatMediaCall.activatedId !== onGoingChatMediaCall?.chat?.chat_id;
    if (inOtherCall) {
      return false;
    }
    return (
      !chatMediaCall.chatMediaInfo ||
      onGoingChatMediaCall?.status === ChatMessageStatus.LOADING ||
      onGoingChatMediaCall?.status === ChatMessageStatus.CALL_REJECTED
    );
  }, [
    chatMediaCall.activatedId,
    chatMediaCall.chatMediaInfo,
    onGoingChatMediaCall?.chat?.chat_id,
    onGoingChatMediaCall?.status,
  ]);

  const actionIcon = useMemo(() => {
    return loading ? (
      <LoadingOutlined></LoadingOutlined>
    ) : onGoingChatMediaCall?.payload_type ===
      ChatMessagePayloadType.MEDIA_AUDIO_CALL ? (
      <CallIcon />
    ) : (
      <VideoIcon />
    );
  }, [onGoingChatMediaCall?.payload_type, loading]);

  const uniqueCallMembers = useMemo(() => {
    return onGoingChatMediaCall?.callMembers?.filter(
      (user, index, self) => index === self.findIndex((t) => t.userId === user.userId)
    );
  }, [onGoingChatMediaCall?.callMembers]);

  const handleAccept = useCallback(async () => {
    if (loading) return;
    setLoading(true);
    await mediaJoin({
      chat,
      ...onGoingChatMediaCall,
    });
    refs.current.chatMediaCall.activeChatMedia(chat.chat_id);
    setLoading(false);
  }, [loading, chat, onGoingChatMediaCall, refs, mediaJoin]);

  useEffect(() => {
    if (showAccept) {
      setLoading(false);
    }
  }, [showAccept]);

  if (!onGoingChatMediaCall?.room_id) {
    return null;
  }

  return (
    <div className={styles.groupJoinCall}>
      <div className={styles.callInfo}>
        <div className={styles.callInfoUsers}>
          {uniqueCallMembers
            ?.slice(0, SHOW_COUNT)
            .map((user) => (
              <MyAvatar
                key={user.userId}
                className={styles.callInfoAvatar}
                src={user.avatar!}
                info={user}
              />
            ))}
          {uniqueCallMembers && uniqueCallMembers.length > SHOW_COUNT && (
            <div className={styles.callInfoCount}>
              +{uniqueCallMembers.length - SHOW_COUNT}
            </div>
          )}
        </div>
        <div className={styles.callInfoText}>
          {onGoingChatMediaCall?.payload_type ===
            ChatMessagePayloadType.MEDIA_AUDIO_CALL && 'Ongoing group voice call'}
          {onGoingChatMediaCall?.payload_type ===
            ChatMessagePayloadType.MEDIA_VIDEO_CALL && 'Ongoing group video call'}
        </div>
      </div>
      {showAccept && (
        <div className={styles.callOperate} onClick={handleAccept}>
          {actionIcon}
          <div>join</div>
        </div>
      )}
      {chatMediaCall.chatMediaInfo?.status === ChatMessageStatus.CALL_PUT_THROUGH &&
        chatMediaCall.activatedId === chat.chat_id && (
          <div
            className={styles.callOperate}
            onClick={() => {
              chatMediaCall.setMediaModalZoomIn(true);
            }}
          >
            {actionIcon}
            <div>Return</div>
          </div>
        )}
    </div>
  );
};
